"use client";

import { localPoint } from "@visx/event";
import type { scaleLinear, scaleTime } from "@visx/scale";
import { useCallback, useRef, useState } from "react";
import type { LineConfig, Margin, TooltipData } from "./chart-context";
type ScaleTime = ReturnType<typeof scaleTime<number>>;
type ScaleLinear = ReturnType<typeof scaleLinear<number>>;
export interface ChartSelection {
  startX: number;
  endX: number;
  startIndex: number;
  endIndex: number;
  active: boolean;
}
interface UseChartInteractionParams {
  xScale: ScaleTime;
  yScale: ScaleLinear;
  data: Record<string, unknown>[];
  lines: LineConfig[];
  margin: Margin;
  xAccessor: (d: Record<string, unknown>) => Date;
  bisectDate: (data: Record<string, unknown>[], date: Date, lo: number) => number;
  canInteract: boolean;
}
interface ChartInteractionResult {
  tooltipData: TooltipData | null;
  setTooltipData: React.Dispatch<React.SetStateAction<TooltipData | null>>;
  selection: ChartSelection | null;
  clearSelection: () => void;
  interactionHandlers: {
    onMouseMove?: (event: React.MouseEvent<SVGGElement>) => void;
    onMouseLeave?: () => void;
    onMouseDown?: (event: React.MouseEvent<SVGGElement>) => void;
    onMouseUp?: () => void;
    onTouchStart?: (event: React.TouchEvent<SVGGElement>) => void;
    onTouchMove?: (event: React.TouchEvent<SVGGElement>) => void;
    onTouchEnd?: () => void;
  };
  interactionStyle: React.CSSProperties;
}
export function useChartInteraction({
  xScale,
  yScale,
  data,
  lines,
  margin,
  xAccessor,
  bisectDate,
  canInteract
}: UseChartInteractionParams): ChartInteractionResult {
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const [selection, setSelection] = useState<ChartSelection | null>(null);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef<number>(0);
  const resolveTooltipFromX = useCallback((pixelX: number): TooltipData | null => {
    const x0 = xScale.invert(pixelX);
    const index = bisectDate(data, x0, 1);
    const d0 = data[index - 1];
    const d1 = data[index];
    if (!d0) {
      return null;
    }
    let d = d0;
    let finalIndex = index - 1;
    if (d1) {
      const d0Time = xAccessor(d0).getTime();
      const d1Time = xAccessor(d1).getTime();
      if (x0.getTime() - d0Time > d1Time - x0.getTime()) {
        d = d1;
        finalIndex = index;
      }
    }
    const yPositions: Record<string, number> = {};
    for (const line of lines) {
      const value = d[line.dataKey];
      if (typeof value === "number") {
        yPositions[line.dataKey] = yScale(value) ?? 0;
      }
    }
    return {
      point: d,
      index: finalIndex,
      x: xScale(xAccessor(d)) ?? 0,
      yPositions
    };
  }, [xScale, yScale, data, lines, xAccessor, bisectDate]);
  const resolveIndexFromX = useCallback((pixelX_0: number): number => {
    const x0_0 = xScale.invert(pixelX_0);
    const index_0 = bisectDate(data, x0_0, 1);
    const d0_0 = data[index_0 - 1];
    const d1_0 = data[index_0];
    if (!d0_0) {
      return 0;
    }
    if (d1_0) {
      const d0Time_0 = xAccessor(d0_0).getTime();
      const d1Time_0 = xAccessor(d1_0).getTime();
      if (x0_0.getTime() - d0Time_0 > d1Time_0 - x0_0.getTime()) {
        return index_0;
      }
    }
    return index_0 - 1;
  }, [xScale, data, xAccessor, bisectDate]);
  const getChartX = useCallback((event: React.MouseEvent<SVGGElement> | React.TouchEvent<SVGGElement>, touchIndex = 0): number | null => {
    let point: {
      x: number;
      y: number;
    } | null = null;
    if ("touches" in event) {
      const touch = event.touches[touchIndex];
      if (!touch) {
        return null;
      }
      const svg = event.currentTarget.ownerSVGElement;
      if (!svg) {
        return null;
      }
      point = localPoint(svg, touch as unknown as MouseEvent);
    } else {
      point = localPoint(event);
    }
    if (!point) {
      return null;
    }
    return point.x - margin.left;
  }, [margin.left]);

  // --- Mouse handlers ---

  const handleMouseMove = useCallback((event_0: React.MouseEvent<SVGGElement>) => {
    const chartX = getChartX(event_0);
    if (chartX === null) {
      return;
    }
    if (isDraggingRef.current) {
      const startX = Math.min(dragStartXRef.current, chartX);
      const endX = Math.max(dragStartXRef.current, chartX);
      setSelection({
        startX,
        endX,
        startIndex: resolveIndexFromX(startX),
        endIndex: resolveIndexFromX(endX),
        active: true
      });
      return;
    }
    const tooltip = resolveTooltipFromX(chartX);
    if (tooltip) {
      setTooltipData(tooltip);
    }
  }, [getChartX, resolveTooltipFromX, resolveIndexFromX]);
  const handleMouseLeave = useCallback(() => {
    setTooltipData(null);
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
    }
    setSelection(null);
  }, []);
  const handleMouseDown = useCallback((event_1: React.MouseEvent<SVGGElement>) => {
    const chartX_0 = getChartX(event_1);
    if (chartX_0 === null) {
      return;
    }
    isDraggingRef.current = true;
    dragStartXRef.current = chartX_0;
    setTooltipData(null);
    setSelection(null);
  }, [getChartX]);
  const handleMouseUp = useCallback(() => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
    }
    setSelection(null);
  }, []);

  // --- Touch handlers ---

  const handleTouchStart = useCallback((event_2: React.TouchEvent<SVGGElement>) => {
    if (event_2.touches.length === 1) {
      event_2.preventDefault();
      const chartX_1 = getChartX(event_2, 0);
      if (chartX_1 === null) {
        return;
      }
      const tooltip_0 = resolveTooltipFromX(chartX_1);
      if (tooltip_0) {
        setTooltipData(tooltip_0);
      }
    } else if (event_2.touches.length === 2) {
      event_2.preventDefault();
      setTooltipData(null);
      const x0_1 = getChartX(event_2, 0);
      const x1 = getChartX(event_2, 1);
      if (x0_1 === null || x1 === null) {
        return;
      }
      const startX_0 = Math.min(x0_1, x1);
      const endX_0 = Math.max(x0_1, x1);
      setSelection({
        startX: startX_0,
        endX: endX_0,
        startIndex: resolveIndexFromX(startX_0),
        endIndex: resolveIndexFromX(endX_0),
        active: true
      });
    }
  }, [getChartX, resolveTooltipFromX, resolveIndexFromX]);
  const handleTouchMove = useCallback((event_3: React.TouchEvent<SVGGElement>) => {
    if (event_3.touches.length === 1) {
      event_3.preventDefault();
      const chartX_2 = getChartX(event_3, 0);
      if (chartX_2 === null) {
        return;
      }
      const tooltip_1 = resolveTooltipFromX(chartX_2);
      if (tooltip_1) {
        setTooltipData(tooltip_1);
      }
    } else if (event_3.touches.length === 2) {
      event_3.preventDefault();
      const x0_2 = getChartX(event_3, 0);
      const x1_0 = getChartX(event_3, 1);
      if (x0_2 === null || x1_0 === null) {
        return;
      }
      const startX_1 = Math.min(x0_2, x1_0);
      const endX_1 = Math.max(x0_2, x1_0);
      setSelection({
        startX: startX_1,
        endX: endX_1,
        startIndex: resolveIndexFromX(startX_1),
        endIndex: resolveIndexFromX(endX_1),
        active: true
      });
    }
  }, [getChartX, resolveTooltipFromX, resolveIndexFromX]);
  const handleTouchEnd = useCallback(() => {
    setTooltipData(null);
    setSelection(null);
  }, []);
  const clearSelection = useCallback(() => {
    setSelection(null);
  }, []);
  const interactionHandlers = canInteract ? {
    onMouseMove: handleMouseMove,
    onMouseLeave: handleMouseLeave,
    onMouseDown: handleMouseDown,
    onMouseUp: handleMouseUp,
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd
  } : {};
  const interactionStyle: React.CSSProperties = {
    cursor: canInteract ? "crosshair" : "default",
    touchAction: "none"
  };
  return {
    tooltipData,
    setTooltipData,
    selection,
    clearSelection,
    interactionHandlers,
    interactionStyle
  };
}